import { useCallback, useMemo, useRef, useState } from "react";

import QUERY_TAGS from "../search/queryTags";
import useQuery from "../search/useQuery";
import { invalidateTags } from "../search/queryCache";
import { fetchCart, saveCart } from "./cartService";
import {
  decrementQuantity,
  findLine,
  hasLine,
  mapCartSummary,
  removeLine,
  toPayload,
  upsertQuantity,
} from "./cartLines";

export const CART_KEY = "cart:self";

const useCart = ({ enabled = true } = {}) => {
  const [isSaving, setSaving] = useState(false);
  const pendingRef = useRef(null);

  const cart = useQuery({
    key: enabled ? CART_KEY : null,
    tag: QUERY_TAGS.CART,
    enabled,
    fetcher: fetchCart,
  });

  const summary = useMemo(() => mapCartSummary(cart.currentData), [cart.currentData]);

  const basket = useMemo(
    () => summary.carts.flatMap((supplierCart) => supplierCart.lines),
    [summary]
  );

  const basketRef = useRef(basket);
  basketRef.current = basket;

  const takeoverQuantities = useCallback(
    () => pendingRef.current || basketRef.current,
    []
  );

  const contains = useCallback(
    (item) => hasLine(pendingRef.current || basketRef.current, item),
    []
  );

  const quantityOf = useCallback(
    (item) => findLine(pendingRef.current || basketRef.current, item)?.quantity ?? 0,
    []
  );

  const commit = useCallback(
    async (next) => {
      pendingRef.current = next;
      setSaving(true);

      try {
        await saveCart(toPayload(next));

        invalidateTags([QUERY_TAGS.CART]);
        await cart.refetch();

        return { status: "saved" };
      } catch (error) {
        return { status: "error", error };
      } finally {
        pendingRef.current = null;
        setSaving(false);
      }
    },
    [cart]
  );

  const addItems = useCallback(
    async (items, { replace = false, mode = "increment" } = {}) => {
      const rows = (items || []).filter((item) => item?.productId);
      if (!rows.length) return { status: "empty" };

      const seed = replace ? [] : takeoverQuantities();

      const next = rows.reduce(
        (basketSoFar, item) => upsertQuantity(basketSoFar, item, mode),
        seed
      );

      const result = await commit(next);

      return result.status === "saved" ? { status: "added" } : result;
    },
    [commit, takeoverQuantities]
  );

  const setQuantity = useCallback(
    async (item, quantity) => {
      const current = takeoverQuantities();

      const next =
        quantity > 0
          ? upsertQuantity(current, { ...item, quantity }, "set")
          : removeLine(current, item);

      const result = await commit(next);

      return result.status === "saved" ? { status: "updated" } : result;
    },
    [commit, takeoverQuantities]
  );

  const incrementLine = useCallback(
    (item, by = 1) => addItems([{ ...item, quantity: by }], { mode: "increment" }),
    [addItems]
  );

  const decrementLine = useCallback(
    async (item, by = 1) => {
      const current = takeoverQuantities();
      const next = decrementQuantity(current, item, by);

      const result = await commit(next);

      return result.status === "saved" ? { status: "updated" } : result;
    },
    [commit, takeoverQuantities]
  );

  const removeCartLine = useCallback(
    async (item) => {
      const current = takeoverQuantities();
      const next = removeLine(current, item);

      const result = await commit(next);

      return result.status === "saved" ? { status: "removed" } : result;
    },
    [commit, takeoverQuantities]
  );

  const clearCart = useCallback(async () => {
    const result = await commit([]);

    return result.status === "saved" ? { status: "cleared" } : result;
  }, [commit]);

  const removeSupplierCart = useCallback(
    async (supplierCart) => {
      const current = takeoverQuantities();
      const next = (supplierCart?.lines || []).reduce(
        (basketSoFar, line) => removeLine(basketSoFar, line),
        current
      );

      const result = await commit(next);

      return result.status === "saved" ? { status: "removed" } : result;
    },
    [commit, takeoverQuantities]
  );

  return useMemo(
    () => ({
      basket,
      summary,
      itemCount: summary.itemCount,
      contains,
      quantityOf,
      addItems,
      setQuantity,
      incrementLine,
      decrementLine,
      removeLine: removeCartLine,
      removeSupplierCart,
      clearCart,
      isSaving,
      isLoading: cart.isFetching && !cart.isSuccess,
      enabled,
    }),
    [
      basket,
      summary,
      contains,
      quantityOf,
      addItems,
      setQuantity,
      incrementLine,
      decrementLine,
      removeCartLine,
      removeSupplierCart,
      clearCart,
      isSaving,
      cart.isFetching,
      cart.isSuccess,
      enabled,
    ]
  );
};

export default useCart;
